import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom'
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Box, CssBaseline, GlobalStyles, AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemText, Divider, Select, MenuItem, FormControl, InputLabel, Button, TextField, OutlinedInput, Checkbox, CircularProgress, Alert, Switch, FormControlLabel, IconButton, Tabs, Tab } from '@mui/material'
import MapView from './MapView'
import FilterPanel from './FilterPanel'
import { useAuth, MAP_READY_SESSION_KEY } from './AuthProvider'
import RightPanel from './RightPanel'
import MapLegend from './MapLegend'
import RegionReportBar from './RegionReportBar'
import MiniPlaybackTimeline from './ui/MiniPlaybackTimeline'
import TimelinePanel from './TimelinePanel'
import LockIcon from '@mui/icons-material/Lock'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { supabase } from '../services/supabase'
import apiService from '../services/api'
import ErrorBoundary from './ErrorBoundary'
import AdminPage from './admin/AdminPage'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import { FILTER_PANEL_WIDTH_CSS, RIGHT_PANEL_WIDTH_CSS } from '../layout/appChrome'
import { APPLICATION_TYPE_ORDER, APPLICATION_TYPE_PRIORITY } from './applicationStyles'
import { lastActivitySortKey } from '../utils/lastActivitySortKey'
import { CASE_STATUS_OPTIONS } from '../utils/caseStatus'
import { sortSlugsCharlotteMetroFirst } from './mapDefaults'
import { matchesActivityDateFilter, ACTIVITY_DATE_MODE_DEFAULT } from '../utils/activityDateFilter'
import { getDefaultDateFilter } from '../utils/localDate'
import { buildFilterConfig, filterConfigsEqual, normalizeFilterConfig } from '../utils/filterConfig'
import { createLogger } from '../utils/logger'
import {
  setSentryViewTags,
  setSentryCountyTags,
  setSentryFilterTags,
  viewFromTab,
} from '../utils/sentryTracking'
import { posthog, isUserIdentified } from '../services/posthog'
import { useIsPhone } from '../hooks/useIsPhone'
import MobileAppShell from './MobileAppShell'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

const logger = createLogger('AppDashboard')

// HUD removed

// Removed drawerWidth - no longer using drawer

const DATA_OPTIONS = APPLICATION_TYPE_ORDER

/** Matches DB check constraint on v3_report_entries.case_nature */
const CASE_NATURE_OPTIONS = ['Original Case', 'Appealed Case']

const APP_TABS = ['Dashboard', 'Map', 'Timeline', 'Applicants', 'Stats', 'Index', 'Admin']
const NAV_TABS = ['Map', 'Timeline']
const TAB_TO_SLUG = { Map: 'map', Timeline: 'timeline', Admin: 'admin' }
const SLUG_TO_TAB = { map: 'Map', timeline: 'Timeline', admin: 'Admin' }

function readStoredTab() {
  const saved = localStorage.getItem('ls_currentTab')
  if (saved && APP_TABS.includes(saved)) {
    if (NAV_TABS.includes(saved) || saved === 'Admin') return saved
    return 'Map'
  }
  return 'Map'
}

/** Resolve Map | Timeline | Admin from /app/<slug>. Bare /app or unknown → null. */
function tabFromPathname(pathname) {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  const match = normalized.match(/^\/app(?:\/([^/]+))?$/)
  if (!match) return null
  const slug = match[1]
  if (!slug) return null
  return SLUG_TO_TAB[slug] || null
}

function pathForTab(tab) {
  return `/app/${TAB_TO_SLUG[tab] || 'map'}`
}

/** Relative slug for index redirect when user hits /app or /app/. */
function defaultTabSlug(isAdminUser) {
  let tab = readStoredTab()
  if (tab === 'Admin' && !isAdminUser) tab = 'Map'
  return TAB_TO_SLUG[tab] || 'map'
}

function readStoredMunicipalityIds() {
  const saved = localStorage.getItem('ls_municipalityIds')
  if (!saved) return []
  try {
    const parsed = JSON.parse(saved)
    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch {
    return []
  }
}

// Removed custom theme - using CSS custom properties instead

function AppDashboard() {
  const [countyState, setCountyState] = useState({
    allCountiesForDropdown: [], // Populated from backend regions
    accessibleCounties: [], // The user's permissions from the backend (regions)
    isLoadingPermissions: true,
    nameToSlug: {}, // Map from display name -> region slug
    slugToId: {}, // Map from county slug -> county UUID
  });

  const { user, logout, isAdmin } = useAuth();
  // Distinguish "never saved" from intentional empty (`[]`). Empty must not be
  // treated as uninitialized or Deselect All gets overwritten on data reload.
  const countyFilterInitializedRef = useRef(localStorage.getItem('ls_counties') != null)
  const municipalityFilterInitializedRef = useRef(localStorage.getItem('ls_municipalityIds') != null)
  const [dateRange, setDateRange] = useState(() => {
    const saved = localStorage.getItem('ls_dateRange')
    if (saved) return JSON.parse(saved)
    return { ...getDefaultDateFilter().dateRange }
  })
  const [includeAllDates, setIncludeAllDates] = useState(() => {
    const saved = localStorage.getItem('ls_includeAllDates')
    if (saved) return JSON.parse(saved)
    return getDefaultDateFilter().includeAllDates
  })
  const [activityDateMode, setActivityDateMode] = useState(() => {
    const saved = localStorage.getItem('ls_activityDateMode')
    if (saved === 'first' || saved === 'overlap' || saved === 'last') return saved
    return ACTIVITY_DATE_MODE_DEFAULT
  })
  const [selectedCountySlugs, setSelectedCountySlugs] = useState(() => {
    const saved = localStorage.getItem('ls_counties')
    return saved ? JSON.parse(saved) : []
  })
  const [selectedDataTypes, setSelectedDataTypes] = useState(() => {
    const saved = localStorage.getItem('ls_dataTypes')
    if (saved) {
      const parsed = JSON.parse(saved);
      const mapCategoryName = (name) => {
        if (name === 'Conditional/Special Use Permit') return 'CUP/SUP';
        if (name === 'Comprehensive Plan Amendment') return 'CPA';
        return name;
      };
      return parsed.map(mapCategoryName);
    }
    return DATA_OPTIONS;
  })
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const saved = localStorage.getItem('ls_statuses')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map((s) => (s === 'Continuing' ? 'Pending' : s))
        }
      } catch {
        /* ignore */
      }
    }
    return [...CASE_STATUS_OPTIONS]
  })
  
  const [dealSize, setDealSize] = useState(() => {
    const saved = localStorage.getItem('ls_dealSize')
    return saved ? JSON.parse(saved) : {
      acres: { operator: '', value: '' },
      units: { operator: '', value: '' },
      density: { operator: '', value: '' }
    }
  })

  const [municipalityOptionsBySlug, setMunicipalityOptionsBySlug] = useState({})
  const [selectedMunicipalityIds, setSelectedMunicipalityIds] = useState(readStoredMunicipalityIds)

  /** `counties` = county checkboxes only (legacy). `counties_and_municipalities` = tree + muni filter. */
  const [locationFilterMode, setLocationFilterMode] = useState(() => {
    const saved = localStorage.getItem('ls_locationFilterMode')
    if (saved === 'counties' || saved === 'counties_and_municipalities') return saved
    return 'counties'
  })

  const allMunicipalityIds = useMemo(
    () => [...new Set(Object.values(municipalityOptionsBySlug).flat().filter((m) => m.is_published !== false).map((m) => m.id))],
    [municipalityOptionsBySlug]
  )


  const [selectedCaseNatures, setSelectedCaseNatures] = useState(() => {
    const saved = localStorage.getItem('ls_caseNatures')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed) && parsed.length > 0) return parsed
      } catch {
        /* ignore */
      }
    }
    return [...CASE_NATURE_OPTIONS]
  })

  const location = useLocation()
  const navigate = useNavigate()
  const pathTab = tabFromPathname(location.pathname)
  // While /app redirects, or non-admin hits /app/admin, show Map until navigate settles.
  const currentTab =
    pathTab && !(pathTab === 'Admin' && !isAdmin) ? pathTab : 'Map'

  const [mapReady, setMapReady] = useState(() => {
    try {
      return sessionStorage.getItem(MAP_READY_SESSION_KEY) === '1'
    } catch {
      return false
    }
  })
  const handleMapReady = useCallback(() => {
    setMapReady(true)
    try {
      sessionStorage.setItem(MAP_READY_SESSION_KEY, '1')
    } catch {
      /* ignore */
    }
  }, [])
  const selectionHydratedRef = useRef(false)
  
  // Persist filters and view state to localStorage
  useEffect(() => localStorage.setItem('ls_dateRange', JSON.stringify(dateRange)), [dateRange])
  useEffect(() => localStorage.setItem('ls_includeAllDates', JSON.stringify(includeAllDates)), [includeAllDates])
  useEffect(() => localStorage.setItem('ls_activityDateMode', activityDateMode), [activityDateMode])
  useEffect(() => {
    // Don't write the initial `[]` before first API hydrate — that would look like
    // an intentional Deselect All and block the default-all-counties path.
    if (!countyFilterInitializedRef.current) return
    localStorage.setItem('ls_counties', JSON.stringify(selectedCountySlugs))
  }, [selectedCountySlugs])
  useEffect(() => localStorage.setItem('ls_dataTypes', JSON.stringify(selectedDataTypes)), [selectedDataTypes])
  useEffect(() => localStorage.setItem('ls_statuses', JSON.stringify(selectedStatuses)), [selectedStatuses])
  useEffect(() => localStorage.setItem('ls_dealSize', JSON.stringify(dealSize)), [dealSize])
  useEffect(() => localStorage.setItem('ls_caseNatures', JSON.stringify(selectedCaseNatures)), [selectedCaseNatures])
  useEffect(() => localStorage.setItem('ls_locationFilterMode', locationFilterMode), [locationFilterMode])
  useEffect(() => {
    if (!municipalityFilterInitializedRef.current) return
    localStorage.setItem('ls_municipalityIds', JSON.stringify(selectedMunicipalityIds))
  }, [selectedMunicipalityIds])
  useEffect(() => localStorage.setItem('ls_currentTab', currentTab), [currentTab])
  const prevTabRef = useRef(currentTab)
  useEffect(() => {
    if (!isUserIdentified()) return
    // Only fire when the URL actually resolved to a real tab (skip bare /app bounce).
    if (!pathTab || (pathTab === 'Admin' && !isAdmin)) return
    const prev = prevTabRef.current
    prevTabRef.current = currentTab
    if (prev !== currentTab) {
      posthog.capture('tab_switched', { from_tab: prev, to_tab: currentTab })
    }
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      tab: currentTab,
    })
  }, [currentTab, pathTab, isAdmin])

  const [selectedProperty, setSelectedProperty] = useState(null)
  const [countyCounts, setCountyCounts] = useState({})
  const [rawApplications, setRawApplications] = useState([])
  const [userActions, setUserActions] = useState([])
  const [userLists, setUserLists] = useState([])
  const [userSavedFilters, setUserSavedFilters] = useState([])
  const [activeSavedFilterId, setActiveSavedFilterId] = useState(null)
  const [baselineFilterConfig, setBaselineFilterConfig] = useState(null)
  const pendingBaselineForIdRef = useRef(null)
  const [activeCollection, setActiveCollection] = useState('all')
  const [selectedApplication, setSelectedApplication] = useState(null)
  const [playbackDate, setPlaybackDate] = useState(null)
  const [leftPanelOpen, setLeftPanelOpen] = useState(true)
  
  // Current region slug derived for the map to focus on (can just use the first selected or null)
  const selectedRegionSlug = selectedCountySlugs.length > 0 ? selectedCountySlugs[0] : null;
  
  const path = location.pathname
  const showSidebar = true
  const showInitialLoading = !mapReady

  useEffect(() => {
    setSentryViewTags({ pathname: path, tab: currentTab })
  }, [path, currentTab])

  useEffect(() => {
    setSentryCountyTags(selectedCountySlugs, countyState.slugToId)
  }, [selectedCountySlugs, countyState.slugToId])

  useEffect(() => {
    setSentryFilterTags({
      caseTypes: selectedDataTypes,
      includeAllDates,
      dateRange,
    })
  }, [selectedDataTypes, includeAllDates, dateRange])

  const filtersTimerRef = useRef(null)
  useEffect(() => {
    if (!isUserIdentified()) return
    if (filtersTimerRef.current) clearTimeout(filtersTimerRef.current)
    filtersTimerRef.current = setTimeout(() => {
      posthog.capture('filters_applied', {
        counties_selected: selectedCountySlugs,
        data_types: selectedDataTypes,
        date_range: includeAllDates ? 'all' : dateRange,
        statuses: selectedStatuses,
        has_deal_size_filter: !!(dealSize?.acres?.value || dealSize?.units?.value),
        municipality_count: selectedMunicipalityIds.length,
        location_filter_mode: locationFilterMode,
      })
    }, 400)
    return () => { if (filtersTimerRef.current) clearTimeout(filtersTimerRef.current) }
  }, [selectedCountySlugs, selectedDataTypes, includeAllDates, dateRange, selectedStatuses, dealSize, selectedMunicipalityIds, locationFilterMode])

  useEffect(() => {
    if (!user) {
      setCountyState(prevState => ({ ...prevState, isLoadingPermissions: false }));
      return;
    }

    let cancelled = false;

    const mapCategoryName = (name) => {
      if (name === 'Conditional/Special Use Permit') return 'CUP/SUP';
      if (name === 'Comprehensive Plan Amendment') return 'CPA';
      return name;
    };

    const _t0 = performance.now()

    apiService.getMyReports()
      .then(data => {
        if (cancelled) return;

        const allCounties = data.counties || [];
        const accessibleCounties = allCounties.filter(c => c.has_access);

        const allCountiesForDropdown = allCounties.map(c => ({ name: c.name, slug: c.slug, state: c.state }));
        const displayName = (c) => c.state ? `${c.name}, ${c.state}` : c.name;
        const nameToSlug = {};
        const nameToCount = {};
        const slugToId = {};
        allCounties.forEach(c => {
          const dn = displayName(c);
          if (c?.name && c?.slug) nameToSlug[dn] = c.slug;
          if (c?.name) nameToCount[dn] = typeof c.report_count === 'number' ? c.report_count : 0;
          if (c?.slug && c?.id) slugToId[c.slug] = c.id;
        });

        setCountyState({
          allCountiesForDropdown,
          accessibleCounties,
          isLoadingPermissions: false,
          nameToSlug,
          slugToId,
        });
        setCountyCounts(nameToCount);
        {
          const accessibleSlugs = sortSlugsCharlotteMetroFirst(accessibleCounties.map(c => c.slug));
          const accessibleSet = new Set(accessibleSlugs);
          if (countyFilterInitializedRef.current) {
            // Keep intentional selection (including Deselect All → []).
            setSelectedCountySlugs(prev => prev.filter(s => accessibleSet.has(s)));
          } else {
            countyFilterInitializedRef.current = true;
            setSelectedCountySlugs(accessibleSlugs);
          }
        }

        // Reports — map category names and derive county_slug from county_ids
        const slugById = {};
        allCounties.forEach(c => { slugById[c.id] = c.slug; });
        const accessibleSlugs = new Set(accessibleCounties.map(c => c.slug));

        const reports = (data.reports || []).map(app => {
          const cids = app.county_ids || [];
          const slug = cids.map(id => slugById[id]).find(s => accessibleSlugs.has(s)) || slugById[cids[0]] || '';
          let newApp = { ...app, county_slug: slug };
          if (newApp.application_type) newApp.application_type = mapCategoryName(newApp.application_type);
          if (Array.isArray(newApp.category_names)) {
            newApp.category_names = newApp.category_names.map(mapCategoryName);
          } else if (typeof newApp.category_names === 'string') {
            newApp.category_names = [mapCategoryName(newApp.category_names)];
          }
          return newApp;
        });
        setRawApplications(reports);

        const _loadMs = Math.round(performance.now() - _t0)
        logger.info('County data loaded', {
          view: viewFromTab(currentTab),
          action: 'load_county_data',
          county_count: accessibleCounties.length,
          report_count: reports.length,
          load_time_ms: _loadMs,
        })

        if (isUserIdentified()) {
          posthog.capture('initial_data_loaded', {
            report_count: reports.length,
            county_count: accessibleCounties.length,
            load_time_ms: _loadMs,
          })
          posthog.people.set({
            accessible_counties: accessibleCounties.map(c => c.slug),
            county_count: accessibleCounties.length,
          })
        }

        // Municipalities
        const munis = data.municipalities || {};
        setMunicipalityOptionsBySlug(munis);
        const loadedIds = [...new Set(Object.values(munis).flat().filter(m => m.is_published !== false).map(m => m.id))];
        if (municipalityFilterInitializedRef.current) {
          setSelectedMunicipalityIds(prev => {
            if (prev.length === 0) return prev; // intentional Deselect All
            const missing = loadedIds.filter(id => !prev.includes(id));
            return missing.length ? [...prev, ...missing] : prev;
          });
        } else {
          municipalityFilterInitializedRef.current = true;
          setSelectedMunicipalityIds(loadedIds);
        }

        // User actions, lists, and saved filters
        setUserActions(data.user_actions || []);
        setUserLists(data.user_lists || []);
        setUserSavedFilters(data.user_saved_filters || []);
      })
      .catch(err => {
        logger.apiFailure('Failed to load county data', {
          view: 'dashboard',
          action: 'load_county_data',
        }, err);
        if (!cancelled) {
          setCountyState(prev => ({ ...prev, accessibleCounties: [], isLoadingPermissions: false }));
          setRawApplications([]);
          setMunicipalityOptionsBySlug({});
        }
      });

    return () => { cancelled = true };
  }, [user?.id]);

  const handleCountiesChange = useCallback(
    (newSlugs) => {
      countyFilterInitializedRef.current = true
      setSelectedCountySlugs((prevSlugs) => {
        const added = newSlugs.filter((s) => !prevSlugs.includes(s))
        if (added.length > 0 && locationFilterMode === 'counties_and_municipalities') {
          const extra = [
            ...new Set(added.flatMap((s) => (municipalityOptionsBySlug[s] || []).filter((m) => m.is_published !== false).map((m) => m.id))),
          ]
          if (extra.length > 0) {
            municipalityFilterInitializedRef.current = true
            setSelectedMunicipalityIds((pm) => [...new Set([...pm, ...extra])])
          }
        }
        return newSlugs
      })
    },
    [municipalityOptionsBySlug, locationFilterMode]
  )

  const handleMunicipalityIdsChange = useCallback((ids) => {
    municipalityFilterInitializedRef.current = true
    setSelectedMunicipalityIds(ids)
  }, [])

  // Derive filtered applications locally for instantaneous UI updates
  const applications = useMemo(() => {
    const actualDataTypes = selectedDataTypes.filter(item => item !== 'SELECT_ALL');

    // Location: counties mode = county checkboxes only.
    // +Municipalities mode: selected munis match even if the parent county is unchecked
    // (so "Charlotte only" works). County-only cases (no municipality_ids) still require
    // the county checkbox when muni filtering is active.
    let combined = rawApplications.filter((app) => {
      const countyOk = selectedCountySlugs.includes(app.county_slug)
      if (locationFilterMode !== 'counties_and_municipalities') {
        return countyOk
      }
      const mids = app.municipality_ids
      const isCountyOnly = !Array.isArray(mids) || mids.length === 0
      if (selectedMunicipalityIds.length === 0) {
        return countyOk
      }
      const want = new Set(selectedMunicipalityIds.map(String))
      const hasMuniMatch = !isCountyOnly && mids.some((id) => want.has(String(id)))
      if (hasMuniMatch) return true
      return countyOk && isCountyOnly
    })

    // Filter by Data Type
    if (actualDataTypes.length > 0) {
      combined = combined.filter(app => {
        let types = [];
        if (Array.isArray(app.category_names)) types = app.category_names;
        else if (typeof app.category_names === 'string') types = [app.category_names];
        else if (app.application_type) types = [app.application_type];
        
        return types.some(t => actualDataTypes.includes(t));
      }).map(app => {
        let types = [];
        if (Array.isArray(app.category_names)) types = app.category_names;
        else if (typeof app.category_names === 'string') types = [app.category_names];
        else if (app.application_type) types = [app.application_type];

        // Highest-priority type among those active in filters AND present on this case
        const bestType = APPLICATION_TYPE_PRIORITY.find(opt => actualDataTypes.includes(opt) && types.includes(opt));
        
        // Override application_type so map/pins use the filter-aware primary color
        if (bestType) {
          return { ...app, application_type: bestType };
        }
        return app;
      });
    } else {
      return [];
    }

    // Filter by current_status (matches v3_report_entries.current_status)
    if (selectedStatuses.length > 0) {
      combined = combined.filter((app) => selectedStatuses.includes(app.current_status));
    } else {
      return [];
    }

    // Filter by activity vs date range (first / overlap / last); uses first_activity_date & last_activity_date with timeline fallback
    if (!includeAllDates && dateRange.from && dateRange.to) {
      const df = String(dateRange.from).slice(0, 10);
      const dt = String(dateRange.to).slice(0, 10);
      combined = combined.filter((app) => matchesActivityDateFilter(app, df, dt, activityDateMode));
    }

    // Case nature (Original vs Appealed); both selected = no extra filter
    if (selectedCaseNatures.length === 0) {
      return [];
    }
    if (selectedCaseNatures.length < CASE_NATURE_OPTIONS.length) {
      combined = combined.filter((app) => {
        const v = app.case_nature;
        return v != null && v !== '' && selectedCaseNatures.includes(String(v));
      });
    }

    // Filter by Deal Size
    if (dealSize) {
      if (dealSize.acres.operator && dealSize.acres.value !== '') {
        const val = parseFloat(dealSize.acres.value);
        if (!isNaN(val)) {
          combined = combined.filter(app => {
            const appVal = parseFloat(app.acres);
            if (isNaN(appVal)) return false;
            return dealSize.acres.operator === 'less' ? appVal < val : appVal > val;
          });
        }
      }
      if (dealSize.units.operator && dealSize.units.value !== '') {
        const val = parseFloat(dealSize.units.value);
        if (!isNaN(val)) {
          combined = combined.filter(app => {
            const appVal = parseFloat(app.residential_units);
            if (isNaN(appVal)) return false;
            return dealSize.units.operator === 'less' ? appVal < val : appVal > val;
          });
        }
      }
      if (dealSize.density.operator && dealSize.density.value !== '') {
        const val = parseFloat(dealSize.density.value);
        if (!isNaN(val)) {
          combined = combined.filter(app => {
            const appVal = parseFloat(app.proposed_density);
            if (isNaN(appVal)) return false;
            return dealSize.density.operator === 'less' ? appVal < val : appVal > val;
          });
        }
      }
    }

    // Newest last activity first (matches timeline + cases list)
    combined.sort((a, b) => {
      const d = lastActivitySortKey(b) - lastActivitySortKey(a);
      if (d !== 0) return d;
      return String(a.case_number || '').localeCompare(String(b.case_number || ''));
    });

    return combined;
  }, [
    rawApplications,
    selectedCountySlugs,
    selectedDataTypes,
    selectedStatuses,
    includeAllDates,
    dateRange,
    activityDateMode,
    selectedCaseNatures,
    dealSize,
    selectedMunicipalityIds,
    locationFilterMode,
  ]);

  // Lookup maps for user actions and lists
  const actionsByReportId = useMemo(() => {
    const map = {};
    (userActions || []).forEach(a => { map[a.report_id] = a; });
    return map;
  }, [userActions]);

  const listItemsByListId = useMemo(() => {
    const map = {};
    (userLists || []).forEach(l => {
      map[l.id] = new Set(l.report_ids || []);
    });
    return map;
  }, [userLists]);

  // Apply collection filter on top of existing filters
  const reportInList = useCallback((report, reportIds) => {
    if (reportIds.has(report.id)) return true;
    if (report.published_report_id && reportIds.has(report.published_report_id)) return true;
    return false;
  }, []);

  const collectionFilteredApplications = useMemo(() => {
    if (activeCollection === 'followed') {
      return applications.filter(r => {
        const action = actionsByReportId[r.id] ?? actionsByReportId[r.published_report_id];
        return action?.is_following;
      });
    }
    if (activeCollection.startsWith('list:')) {
      const listId = activeCollection.slice(5);
      const reportIds = listItemsByListId[listId] || new Set();
      return applications.filter(r => reportInList(r, reportIds));
    }
    // 'all' — pass everything; RightPanel handles triage sections
    return applications;
  }, [applications, activeCollection, actionsByReportId, listItemsByListId, reportInList]);

  const allCasesCount = applications.length;
  const followedCount = useMemo(() => {
    return applications.filter((r) => {
      const action = actionsByReportId[r.id] ?? actionsByReportId[r.published_report_id];
      return action?.is_following;
    }).length;
  }, [applications, actionsByReportId]);

  // Total cases in active collection before left-panel filters
  const collectionTotalApplications = useMemo(() => {
    if (activeCollection === 'followed') {
      return rawApplications.filter(r => {
        const action = actionsByReportId[r.id] ?? actionsByReportId[r.published_report_id];
        return action?.is_following;
      });
    }
    if (activeCollection.startsWith('list:')) {
      const listId = activeCollection.slice(5);
      const reportIds = listItemsByListId[listId] || new Set();
      return rawApplications.filter(r => reportInList(r, reportIds));
    }
    return rawApplications;
  }, [rawApplications, activeCollection, actionsByReportId, listItemsByListId, reportInList]);

  const filtersActive = collectionFilteredApplications.length < collectionTotalApplications.length;

  const currentFilterConfig = useMemo(() => buildFilterConfig({
    selectedCountySlugs,
    selectedMunicipalityIds,
    locationFilterMode,
    selectedDataTypes,
    selectedStatuses,
    includeAllDates,
    dateRange,
    activityDateMode,
    dealSize,
    selectedCaseNatures,
  }), [
    selectedCountySlugs,
    selectedMunicipalityIds,
    locationFilterMode,
    selectedDataTypes,
    selectedStatuses,
    includeAllDates,
    dateRange,
    activityDateMode,
    dealSize,
    selectedCaseNatures,
  ]);

  const filtersModified = useMemo(() => {
    if (!activeSavedFilterId || !baselineFilterConfig) return false;
    return !filterConfigsEqual(currentFilterConfig, baselineFilterConfig);
  }, [currentFilterConfig, baselineFilterConfig, activeSavedFilterId]);

  useEffect(() => {
    if (!pendingBaselineForIdRef.current || pendingBaselineForIdRef.current !== activeSavedFilterId) return;
    setBaselineFilterConfig(normalizeFilterConfig(currentFilterConfig));
    pendingBaselineForIdRef.current = null;
  }, [activeSavedFilterId, currentFilterConfig]);

  const applyFilterConfig = useCallback((config) => {
    if (!config) return;
    if (config.selectedCountySlugs != null) {
      countyFilterInitializedRef.current = true;
      setSelectedCountySlugs(config.selectedCountySlugs);
    }
    if (config.selectedMunicipalityIds != null) {
      municipalityFilterInitializedRef.current = true;
      setSelectedMunicipalityIds(config.selectedMunicipalityIds);
    }
    if (config.locationFilterMode != null) setLocationFilterMode(config.locationFilterMode);
    if (config.selectedDataTypes != null) setSelectedDataTypes(config.selectedDataTypes);
    if (config.selectedStatuses != null) setSelectedStatuses(config.selectedStatuses);
    if (config.includeAllDates != null) setIncludeAllDates(config.includeAllDates);
    if (config.dateRange != null) setDateRange({ ...config.dateRange });
    if (config.activityDateMode != null) setActivityDateMode(config.activityDateMode);
    if (config.dealSize != null) {
      setDealSize({
        acres: { ...(config.dealSize.acres || { operator: '', value: '' }) },
        units: { ...(config.dealSize.units || { operator: '', value: '' }) },
        density: { ...(config.dealSize.density || { operator: '', value: '' }) },
      });
    }
    if (config.selectedCaseNatures != null) setSelectedCaseNatures(config.selectedCaseNatures);
  }, []);

  const handleLoadSavedFilter = useCallback((filter) => {
    applyFilterConfig(filter.filter_config);
    setBaselineFilterConfig(null);
    pendingBaselineForIdRef.current = filter.id;
    setActiveSavedFilterId(filter.id);
  }, [applyFilterConfig]);

  const handleSaveCurrentFilters = useCallback(async (name) => {
    const created = await apiService.createSavedFilter(name.trim(), currentFilterConfig);
    setUserSavedFilters((prev) => [...prev, created].sort((a, b) => a.position - b.position));
    setBaselineFilterConfig(normalizeFilterConfig(currentFilterConfig));
    pendingBaselineForIdRef.current = null;
    setActiveSavedFilterId(created.id);
    return created;
  }, [currentFilterConfig]);

  const handleRenameSavedFilter = useCallback(async (filterId, name) => {
    const updated = await apiService.updateSavedFilter(filterId, { name: name.trim() });
    setUserSavedFilters((prev) => prev.map((f) => (f.id === filterId ? { ...f, ...updated } : f)));
    return updated;
  }, []);

  const handleOverwriteSavedFilter = useCallback(async (filterId) => {
    const updated = await apiService.updateSavedFilter(filterId, { filter_config: currentFilterConfig });
    setUserSavedFilters((prev) => prev.map((f) => (f.id === filterId ? { ...f, ...updated } : f)));
    setBaselineFilterConfig(normalizeFilterConfig(currentFilterConfig));
    pendingBaselineForIdRef.current = null;
    setActiveSavedFilterId(filterId);
    return updated;
  }, [currentFilterConfig]);

  const handleDeleteSavedFilter = useCallback(async (filterId) => {
    await apiService.deleteSavedFilter(filterId);
    setUserSavedFilters((prev) => prev.filter((f) => f.id !== filterId));
    setActiveSavedFilterId((prev) => {
      if (prev === filterId) {
        setBaselineFilterConfig(null);
        pendingBaselineForIdRef.current = null;
        return null;
      }
      return prev;
    });
  }, []);

  const handleResetFilters = useCallback(() => {
    const defaultDateFilter = getDefaultDateFilter()
    setActiveSavedFilterId(null);
    setBaselineFilterConfig(null);
    pendingBaselineForIdRef.current = null;
    countyFilterInitializedRef.current = true;
    municipalityFilterInitializedRef.current = true;
    setSelectedCountySlugs(sortSlugsCharlotteMetroFirst(countyState.accessibleCounties.map(c => c.slug)));
    setSelectedDataTypes(DATA_OPTIONS);
    setSelectedStatuses([...CASE_STATUS_OPTIONS]);
    setIncludeAllDates(defaultDateFilter.includeAllDates);
    setDateRange({ ...defaultDateFilter.dateRange });
    setActivityDateMode(ACTIVITY_DATE_MODE_DEFAULT);
    setDealSize({
      acres: { operator: '', value: '' },
      units: { operator: '', value: '' },
      density: { operator: '', value: '' }
    });
    setSelectedCaseNatures([...CASE_NATURE_OPTIONS]);
    setLocationFilterMode('counties');
    setSelectedMunicipalityIds(
      [...new Set(Object.values(municipalityOptionsBySlug).flat().filter((m) => m.is_published !== false).map((m) => m.id))]
    );
  }, [countyState.accessibleCounties, municipalityOptionsBySlug]);

  // Restore selected report once after data loads
  useEffect(() => {
    if (selectionHydratedRef.current || countyState.isLoadingPermissions) return
    if (rawApplications.length === 0) return

    selectionHydratedRef.current = true

    const savedId = localStorage.getItem('ls_selectedApplicationId')
    if (!savedId) return

    const found = applications.find((app) => String(app.id) === savedId)
    if (found) {
      setSelectedApplication(found)
    } else {
      localStorage.removeItem('ls_selectedApplicationId')
    }
  }, [applications, rawApplications, countyState.isLoadingPermissions])

  useEffect(() => {
    if (!selectionHydratedRef.current) return
    if (selectedApplication?.id) {
      localStorage.setItem('ls_selectedApplicationId', String(selectedApplication.id))
    } else {
      localStorage.removeItem('ls_selectedApplicationId')
    }
  }, [selectedApplication])

  useEffect(() => {
    if (rawApplications.length === 0) return
    logger.info('Filters applied', {
      view: viewFromTab(currentTab),
      action: 'apply_filters',
      case_types: selectedDataTypes.filter(t => t !== 'SELECT_ALL').join(',') || 'none',
      county_slugs: selectedCountySlugs.join(',') || 'none',
      date_range: includeAllDates ? 'all' : `${dateRange.from || '…'}–${dateRange.to || '…'}`,
      result_count: applications.length,
      raw_count: rawApplications.length,
    })
  }, [applications.length])

  const handleDateToggle = (event) => {
    const isChecked = event.target.checked;
    setIncludeAllDates(isChecked);
    // When toggling to "All Dates", clear the specific date range
    if (isChecked) {
      setDateRange({ from: '', to: '' });
      // Also clear playback filter so all pins show
      setPlaybackDate(null);
    }
  };

  // Count of reports without backend coordinates (not shown on the map)
  const hiddenCount = useMemo(() => {
    const hasCoords = (app) => {
      const preLat = app.geocode_lat;
      const preLng = app.geocode_lng;
      return typeof preLat === 'number' && typeof preLng === 'number';
    };
    return (applications || []).reduce((acc, app) => acc + (hasCoords(app) ? 0 : 1), 0);
  }, [applications]);

  // Reset playback when switching to All Time
  useEffect(() => {
    if (includeAllDates) {
      setPlaybackDate(null);
    }
  }, [includeAllDates]);

  // Reset playback when selected counties change
  useEffect(() => {
    setPlaybackDate(null);
  }, [selectedCountySlugs]);

  // Reset playback when selected data types change
  useEffect(() => {
    setPlaybackDate(null);
  }, [selectedDataTypes]);

  const handleRegionSelect = useCallback((regionSlug) => {
    if (!selectedCountySlugs.includes(regionSlug)) {
      handleCountiesChange([...selectedCountySlugs, regionSlug]);
    }
  }, [selectedCountySlugs, handleCountiesChange]);

  const showCasesRightPanel =
    currentTab === 'Map' ||
    currentTab === 'Timeline' ||
    currentTab === 'Applicants' ||
    currentTab === 'Stats';

  /** Filters scope Map/Timeline/Stats/Applicants/Index — not the top-level Dashboard or Admin. */
  const showFilterSidebar = currentTab !== 'Dashboard' && currentTab !== 'Admin'
  const accessibleRegionSlugs = useMemo(
    () => countyState.accessibleCounties.map((r) => r.slug),
    [countyState.accessibleCounties],
  )
  const handleExpandFilters = useCallback(() => setLeftPanelOpen(true), [])
  const showExpandFilters = showFilterSidebar && !leftPanelOpen

  const isPhone = useIsPhone()
  const [mobilePane, setMobilePane] = useState('map')
  const mobileReturnPaneRef = useRef('map')
  const [mapFocusNonce, setMapFocusNonce] = useState(0)

  const handleShowOnMap = useCallback(() => {
    if (isPhone) {
      mobileReturnPaneRef.current = 'map'
      setMobilePane('map')
    }
    if (currentTab !== 'Map') navigate(pathForTab('Map'))
    setMapFocusNonce((n) => n + 1)
  }, [isPhone, currentTab, navigate])

  // Kill leftover document scroll / accidental pinch-zoom state on phone shell load.
  useEffect(() => {
    if (!isPhone) return
    const reset = () => {
      window.scrollTo(0, 0)
      document.documentElement.scrollLeft = 0
      document.body.scrollLeft = 0
    }
    reset()
    const t = window.setTimeout(reset, 100)
    return () => window.clearTimeout(t)
  }, [isPhone])

  useEffect(() => {
    if (!isPhone || currentTab === 'Admin') return
    if (currentTab === 'Timeline') {
      setMobilePane((prev) => (prev === 'cases' || prev === 'filters' ? prev : 'timeline'))
    } else if (currentTab === 'Map') {
      setMobilePane((prev) => (prev === 'cases' || prev === 'filters' ? prev : 'map'))
    }
  }, [currentTab, isPhone])

  const handleMobilePaneChange = useCallback((next) => {
    if (next === 'map') {
      mobileReturnPaneRef.current = 'map'
      setMobilePane('map')
      if (currentTab !== 'Map') navigate(pathForTab('Map'))
      return
    }
    if (next === 'timeline') {
      mobileReturnPaneRef.current = 'timeline'
      setMobilePane('timeline')
      if (currentTab !== 'Timeline') navigate(pathForTab('Timeline'))
      return
    }
    if (next === 'filters') {
      setMobilePane((prev) => {
        if (prev === 'map' || prev === 'timeline') mobileReturnPaneRef.current = prev
        return 'filters'
      })
      return
    }
    setMobilePane(next)
  }, [currentTab, navigate])

  const handleMobileSelectApplication = useCallback((app) => {
    if (app) {
      setSelectedApplication(app)
      setMobilePane('cases')
      return
    }
    setSelectedApplication(null)
  }, [])

  const handleMobileFiltersClose = useCallback(() => {
    setMobilePane(mobileReturnPaneRef.current || 'map')
  }, [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
      <CssBaseline />
      <GlobalStyles styles={{
        'html, body, #root': {
          overflow: 'hidden',
          overflowX: 'clip',
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          margin: 0,
          padding: 0,
          overscrollBehavior: 'none',
        },
        '@supports (height: 100dvh)': {
          'html, body, #root': {
            height: '100dvh',
            maxHeight: '100dvh',
          },
        },
      }} />
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          visibility: showInitialLoading ? 'hidden' : 'visible',
        }}
      >
      {showSidebar && !isPhone && (
        <AppBar position="static" elevation={0} sx={{
          zIndex: 1201,
          bgcolor: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          color: '#111827'
        }}>
          <Toolbar variant="dense" sx={{ justifyContent: 'space-between', minHeight: '48px', px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <img src="/ls-logo.svg" alt="LandSentry" style={{ height: '24px', marginRight: '32px' }} />
              <Tabs 
                value={currentTab} 
                onChange={(e, v) => navigate(pathForTab(v))} 
                sx={{ 
                  minHeight: '48px', 
                  '& .MuiTab-root': { 
                    minHeight: '48px', 
                    textTransform: 'none', 
                    fontWeight: 600, 
                    color: '#6b7280',
                    fontSize: '0.875rem',
                    py: 0
                  }, 
                  '& .Mui-selected': { color: '#7454A5 !important' }, 
                  '& .MuiTabs-indicator': { backgroundColor: '#7454A5' } 
                }}
              >
                <Tab label="Map" value="Map" />
                <Tab label="Timeline" value="Timeline" />
                {isAdmin && <Tab label="Admin" value="Admin" />}
              </Tabs>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<LogoutOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
                onClick={logout}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  color: '#b45353',
                  borderColor: '#f0d4d4',
                  bgcolor: '#fdf7f7',
                  borderRadius: '6px',
                  px: 1.25,
                  py: 0.25,
                  '&:hover': {
                    color: '#9f2d2d',
                    borderColor: '#e8b4b4',
                    bgcolor: '#faf0f0',
                  },
                }}
              >
                Log out
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
      )}
      {/* Removed Drawer - data selectors moved to AppBar */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <ErrorBoundary>
          <Routes>
            {/* Paths relative to parent /app/* — shell UI stays mounted; routes only sync the URL. */}
            <Route index element={<Navigate to={defaultTabSlug(isAdmin)} replace />} />
            <Route path="map" element={null} />
            <Route path="timeline" element={null} />
            <Route
              path="admin"
              element={isAdmin ? null : <Navigate to="/app/map" replace />}
            />
            <Route path="*" element={<Navigate to="map" replace />} />
          </Routes>
        </ErrorBoundary>

        {isPhone && currentTab === 'Admin' && isAdmin && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                minHeight: 44,
                borderBottom: '1px solid #e5e7eb',
                bgcolor: '#fff',
              }}
            >
              <IconButton size="small" onClick={() => navigate(pathForTab('Map'))} aria-label="Back to Map">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Admin</Typography>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <ErrorBoundary>
                <AdminPage embedded />
              </ErrorBoundary>
            </Box>
          </Box>
        )}

        {isPhone && currentTab !== 'Admin' && (
          <MobileAppShell
            pane={mobilePane}
            onPaneChange={handleMobilePaneChange}
            logout={logout}
            isAdmin={isAdmin}
            onAdmin={() => navigate(pathForTab('Admin'))}
            filtersActive={filtersActive}
            mapContent={(
              <MapView
                county={null}
                dateRange={dateRange}
                dataTypes={selectedDataTypes}
                embedded
                isVisible={mobilePane === 'map'}
                compactChrome
                regions={countyState.allCountiesForDropdown}
                selectedRegionSlug={selectedRegionSlug}
                viewportCountySlugs={selectedCountySlugs}
                onRegionSelect={handleRegionSelect}
                accessibleRegionSlugs={accessibleRegionSlugs}
                selectedProperty={selectedProperty}
                applications={applications}
                playbackDate={playbackDate}
                includeAllDates={includeAllDates}
                hiddenCount={hiddenCount}
                selectedApplication={selectedApplication}
                onPinClick={handleMobileSelectApplication}
                dataReady={!countyState.isLoadingPermissions}
                onReady={handleMapReady}
                showExpandFilters={false}
                mapFocusNonce={mapFocusNonce}
              />
            )}
            casesContent={(
              <ErrorBoundary>
                <RightPanel
                  embedded
                  externalOpen={true}
                  onOpenChange={() => {}}
                  loading={countyState.isLoadingPermissions}
                  applications={collectionFilteredApplications}
                  allApplicationsCount={applications.length}
                  allCasesCount={allCasesCount}
                  followedCount={followedCount}
                  collectionTotalCount={collectionTotalApplications.length}
                  filtersActive={filtersActive}
                  onResetFilters={handleResetFilters}
                  onSelectProperty={setSelectedProperty}
                  isAccessRestricted={false}
                  selectedApplication={selectedApplication}
                  onSelectApplication={handleMobileSelectApplication}
                  isAdmin={isAdmin}
                  municipalityOptionsBySlug={municipalityOptionsBySlug}
                  activeCollection={activeCollection}
                  onCollectionChange={setActiveCollection}
                  userActions={userActions}
                  userLists={userLists}
                  actionsByReportId={actionsByReportId}
                  onActionChange={setUserActions}
                  onListsChange={setUserLists}
                  onShowOnMap={handleShowOnMap}
                />
              </ErrorBoundary>
            )}
            timelineContent={(
              <TimelinePanel
                embedded
                mode="tab"
                isActive={mobilePane === 'timeline'}
                loading={countyState.isLoadingPermissions}
                applications={applications}
                selectedApplication={selectedApplication}
                onSelectApplication={handleMobileSelectApplication}
                onSelectProperty={setSelectedProperty}
                dateRange={dateRange}
                includeAllDates={includeAllDates}
                showExpandFilters={false}
              />
            )}
            filtersContent={(
              <FilterPanel
                loading={countyState.isLoadingPermissions}
                counties={countyState.accessibleCounties.map(c => ({ name: c.name, slug: c.slug, state: c.state }))}
                selectedCountySlugs={selectedCountySlugs}
                onCountiesChange={handleCountiesChange}
                municipalityOptionsBySlug={municipalityOptionsBySlug}
                selectedMunicipalityIds={selectedMunicipalityIds}
                onMunicipalityIdsChange={handleMunicipalityIdsChange}
                locationFilterMode={locationFilterMode}
                onLocationFilterModeChange={setLocationFilterMode}
                caseTypes={DATA_OPTIONS}
                selectedCaseTypes={selectedDataTypes}
                onCaseTypesChange={setSelectedDataTypes}
                statusOptions={CASE_STATUS_OPTIONS}
                selectedStatuses={selectedStatuses}
                onStatusesChange={setSelectedStatuses}
                dateRange={dateRange}
                includeAllDates={includeAllDates}
                activityDateMode={activityDateMode}
                onActivityDateModeChange={setActivityDateMode}
                onDateChange={({ includeAllDates: newAllDates, from, to }) => {
                  setIncludeAllDates(newAllDates);
                  if (!newAllDates && !from && !to) {
                    setDateRange({ ...getDefaultDateFilter().dateRange });
                  } else {
                    setDateRange({ from, to });
                  }
                }}
                dealSize={dealSize}
                onDealSizeChange={setDealSize}
                caseNatureOptions={CASE_NATURE_OPTIONS}
                selectedCaseNatures={selectedCaseNatures}
                onCaseNaturesChange={setSelectedCaseNatures}
                onReset={handleResetFilters}
                onClose={handleMobileFiltersClose}
                userSavedFilters={userSavedFilters}
                activeSavedFilterId={activeSavedFilterId}
                filtersModified={filtersModified}
                onLoadSavedFilter={handleLoadSavedFilter}
                onSaveCurrentFilters={handleSaveCurrentFilters}
                onRenameSavedFilter={handleRenameSavedFilter}
                onOverwriteSavedFilter={handleOverwriteSavedFilter}
                onDeleteSavedFilter={handleDeleteSavedFilter}
              />
            )}
          />
        )}

        {!isPhone && showSidebar && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
              
              {/* Left Sidebar Filters — fluid width from Omen@150% baseline (18.7%, cap 320) */}
              <Box sx={{ width: showFilterSidebar && leftPanelOpen ? FILTER_PANEL_WIDTH_CSS : 0, transition: 'width 0.25s ease', overflow: 'hidden', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                <Box sx={{ width: '100%', height: '100%', minWidth: 0 }}>
                  <FilterPanel
                    loading={countyState.isLoadingPermissions}
                    counties={countyState.accessibleCounties.map(c => ({ name: c.name, slug: c.slug, state: c.state }))}
                    selectedCountySlugs={selectedCountySlugs}
                    onCountiesChange={handleCountiesChange}
                    municipalityOptionsBySlug={municipalityOptionsBySlug}
                    selectedMunicipalityIds={selectedMunicipalityIds}
                    onMunicipalityIdsChange={handleMunicipalityIdsChange}
                    locationFilterMode={locationFilterMode}
                    onLocationFilterModeChange={setLocationFilterMode}
                    caseTypes={DATA_OPTIONS}
                    selectedCaseTypes={selectedDataTypes}
                    onCaseTypesChange={setSelectedDataTypes}
                    statusOptions={CASE_STATUS_OPTIONS}
                    selectedStatuses={selectedStatuses}
                    onStatusesChange={setSelectedStatuses}
                    dateRange={dateRange}
                    includeAllDates={includeAllDates}
                    activityDateMode={activityDateMode}
                    onActivityDateModeChange={setActivityDateMode}
                    onDateChange={({ includeAllDates: newAllDates, from, to }) => {
                      setIncludeAllDates(newAllDates);
                      if (!newAllDates && !from && !to) {
                        setDateRange({ ...getDefaultDateFilter().dateRange });
                      } else {
                        setDateRange({ from, to });
                      }
                    }}
                    dealSize={dealSize}
                    onDealSizeChange={setDealSize}
                    caseNatureOptions={CASE_NATURE_OPTIONS}
                    selectedCaseNatures={selectedCaseNatures}
                    onCaseNaturesChange={setSelectedCaseNatures}
                    onReset={handleResetFilters}
                    onClose={() => setLeftPanelOpen(false)}
                    userSavedFilters={userSavedFilters}
                    activeSavedFilterId={activeSavedFilterId}
                    filtersModified={filtersModified}
                    onLoadSavedFilter={handleLoadSavedFilter}
                    onSaveCurrentFilters={handleSaveCurrentFilters}
                    onRenameSavedFilter={handleRenameSavedFilter}
                    onOverwriteSavedFilter={handleOverwriteSavedFilter}
                    onDeleteSavedFilter={handleDeleteSavedFilter}
                  />
                </Box>
              </Box>

              {/* Main center area */}
              <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
                <Box sx={{ display: currentTab === 'Map' ? 'flex' : 'none', flex: 1, flexDirection: 'column', position: 'relative' }}>
                  <MapView
                    county={null}
                    dateRange={dateRange}
                    dataTypes={selectedDataTypes}
                    embedded
                    isVisible={currentTab === 'Map'}
                    regions={countyState.allCountiesForDropdown}
                    selectedRegionSlug={selectedRegionSlug}
                    viewportCountySlugs={selectedCountySlugs}
                    onRegionSelect={handleRegionSelect}
                    accessibleRegionSlugs={accessibleRegionSlugs}
                    selectedProperty={selectedProperty}
                    applications={applications}
                    playbackDate={playbackDate}
                    includeAllDates={includeAllDates}
                    hiddenCount={hiddenCount}
                    selectedApplication={selectedApplication}
                    onPinClick={setSelectedApplication}
                    dataReady={!countyState.isLoadingPermissions}
                    onReady={handleMapReady}
                    showExpandFilters={showExpandFilters}
                    onExpandFilters={handleExpandFilters}
                    mapFocusNonce={mapFocusNonce}
                  />
                </Box>

                <Box sx={{ display: currentTab === 'Timeline' ? 'flex' : 'none', flex: 1, overflow: 'hidden', flexDirection: 'column' }}>
                  <TimelinePanel 
                    embedded
                    mode="tab"
                    isActive={currentTab === 'Timeline'}
                    loading={countyState.isLoadingPermissions}
                    applications={applications}
                    selectedApplication={selectedApplication}
                    onSelectApplication={setSelectedApplication}
                    onSelectProperty={setSelectedProperty}
                    dateRange={dateRange}
                    includeAllDates={includeAllDates}
                    showExpandFilters={showExpandFilters}
                    onExpandFilters={handleExpandFilters}
                  />
                </Box>

                {currentTab === 'Dashboard' && (
                  <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="h6" color="textSecondary">Dashboard Coming Soon</Typography>
                  </Box>
                )}

                {currentTab === 'Stats' && (
                  <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="h6" color="textSecondary">Stats Coming Soon</Typography>
                  </Box>
                )}

                {currentTab === 'Applicants' && (
                  <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="h6" color="textSecondary">Applicants Coming Soon</Typography>
                  </Box>
                )}

                {currentTab === 'Index' && (
                  <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="h6" color="textSecondary">Index Coming Soon</Typography>
                  </Box>
                )}

                {currentTab === 'Admin' && isAdmin && (
                  <ErrorBoundary>
                    <AdminPage embedded />
                  </ErrorBoundary>
                )}
                
                {/* 
                  MapLegend, RegionReportBar, MiniPlaybackTimeline are hidden for now 
                  to align with the new UI. 
                */}
              </Box>

              {/* Right Panel (Cases / Details) */}
              <ErrorBoundary>
                <Box sx={{ width: showCasesRightPanel ? RIGHT_PANEL_WIDTH_CSS : 0, height: '100%', borderLeft: showCasesRightPanel ? '1px solid #e5e7eb' : 'none', bgcolor: '#ffffff', transition: 'width 0.25s ease', overflow: 'hidden', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                  <RightPanel 
                    embedded
                    externalOpen={true}
                    onOpenChange={() => {}}
                    loading={countyState.isLoadingPermissions}
                    applications={collectionFilteredApplications}
                    allApplicationsCount={applications.length}
                    allCasesCount={allCasesCount}
                    followedCount={followedCount}
                    collectionTotalCount={collectionTotalApplications.length}
                    filtersActive={filtersActive}
                    onResetFilters={handleResetFilters}
                    onSelectProperty={setSelectedProperty}
                    isAccessRestricted={false}
                    selectedApplication={selectedApplication}
                    onSelectApplication={setSelectedApplication}
                    isAdmin={isAdmin}
                    municipalityOptionsBySlug={municipalityOptionsBySlug}
                    activeCollection={activeCollection}
                    onCollectionChange={setActiveCollection}
                    userActions={userActions}
                    userLists={userLists}
                    actionsByReportId={actionsByReportId}
                    onActionChange={setUserActions}
                    onListsChange={setUserLists}
                    onShowOnMap={handleShowOnMap}
                  />
                </Box>
              </ErrorBoundary>

            </Box>
          </Box>
        )}
      </Box>
      </Box>

      {showInitialLoading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#ffffff',
            zIndex: 9999,
          }}
        >
          <CircularProgress sx={{ color: '#7454A5' }} />
        </Box>
      )}
      
      {/* MiniPlaybackTimeline now rendered inside map container */}
    </Box>
  )
}

export default AppDashboard 